import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getObjections, getObjectionDashboard } from '../../../api';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import ObjectionDashboardPanel from '../../../components/ObjectionDashboardPanel';
import FiltersDrawer from '../../../components/FiltersDrawer';
import { parseMultiSelect, toApiCompanyQueryParam, toApiScalarFilter } from '../../../utils/filtersApi';
import PageHeader from '../../../components/PageHeader';
const OBJECTIONS_DATA = [{
  id: 1,
  serff_tracking_id: 'AMSI-134913394',
  company_name: 'ASI Lloyds',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'HO03 Homeowners — HO03.0 Homeowners (Including Mobile Homeowners)',
  submitted_date: 'Apr 15, 2026',
  objection_letter_date: 'Apr 17, 2026',
  respond_by_date: 'Apr 23, 2026',
  objection_letter_status: 'PI Requested',
  total_objections: 2,
  responded: 0,
  isResponded: false,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 15',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 16',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 17',
    status: 'active'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 23',
    status: 'pending'
  }, {
    step: 5,
    label: 'Under review',
    date: '—',
    status: 'pending'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'The Texas Department of Insurance has reviewed the above-referenced filing and has identified issues requiring additional information or revision before the filing can be approved. Please address the following objections within the specified timeframe. Failure to respond by the deadline will result in the filing being returned as incomplete.',
  objections: [{
    num: 1,
    status: 'No response filed',
    text: 'Please provide the actuarial memorandum supporting the proposed rate change. The filing does not include sufficient documentation to demonstrate that the rates are not excessive, inadequate, or unfairly discriminatory as required under Texas Insurance Code §2251.051. Additionally, provide loss development factors and trend selections with supporting data for each line of business included in this filing.',
    response: null
  }, {
    num: 2,
    status: 'No response filed',
    text: 'The classification plan changes included in this filing affect previously approved rates. A full actuarial justification is required per 28 TAC §5.9312. Please provide a comparison of the proposed classification relativities versus the currently approved relativities, along with credibility-weighted indications for each classification variable used in the rating plan.',
    response: null
  }]
}, {
  id: 2,
  serff_tracking_id: 'FARM-134905551',
  company_name: 'Fire Insurance Exchange',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'FR01 Fire — FR01.0 Fire/Property',
  submitted_date: 'Apr 12, 2026',
  objection_letter_date: 'Apr 14, 2026',
  respond_by_date: 'Apr 20, 2026',
  objection_letter_status: 'Responded',
  total_objections: 1,
  responded: 1,
  isResponded: true,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 12',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 13',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 14',
    status: 'done'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 20',
    status: 'done'
  }, {
    step: 5,
    label: 'Under review',
    date: 'Apr 21',
    status: 'active'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'The Texas Department of Insurance has reviewed the above-referenced filing and has identified issues requiring additional information or revision before the filing can be approved.',
  objections: [{
    num: 1,
    status: 'Response filed Apr 20',
    text: 'The loss cost multiplier submitted does not reconcile with the expense exhibits provided. Please provide a revised exhibit demonstrating how the LCM was derived and confirm the profit loading used is consistent with the company\'s actual expense experience over the past three policy years.',
    related: 'The loss cost multiplier submitted does not reconcile with the expense exhibits provided.',
    response: 'We have revised Exhibit B to clearly show the LCM derivation. The LCM of 1.312 is computed as: (1 + expense ratio of 0.287) / (1 âˆ’ profit load of 0.025). The profit loading of 2.5% reflects the company\'s three-year average return on equity target. Please see the attached revised actuarial memorandum, Appendix C, for the full expense reconciliation table.',
    changed: 'Exhibit B — LCM derivation revised. Actuarial Memorandum Appendix C — expense reconciliation added.'
  }]
}, {
  id: 3,
  serff_tracking_id: 'FORE-134912216',
  company_name: 'Foremost County Mutual',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'HO03 Homeowners',
  submitted_date: 'Apr 14, 2026',
  objection_letter_date: 'Apr 16, 2026',
  respond_by_date: 'Apr 23, 2026',
  objection_letter_status: 'PI Requested',
  total_objections: 1,
  responded: 0,
  isResponded: false,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 14',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 15',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 16',
    status: 'active'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 23',
    status: 'pending'
  }, {
    step: 5,
    label: 'Under review',
    date: '—',
    status: 'pending'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'The Texas Department of Insurance requests justification for relativities selections used in the proposed rating plans.',
  objections: [{
    num: 1,
    status: 'No response filed',
    text: 'Please provide detailed territorial analysis and credibility calculations supporting the revised territory boundaries and relativities selections proposed in this filing.',
    response: null
  }]
}, {
  id: 4,
  serff_tracking_id: 'GAIC-134905445',
  company_name: 'GoAuto Insurance Company',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'PA01 Private Passenger Auto',
  submitted_date: 'Apr 15, 2026',
  objection_letter_date: 'Apr 17, 2026',
  respond_by_date: 'Apr 23, 2026',
  objection_letter_status: 'PI Requested',
  total_objections: 1,
  responded: 0,
  isResponded: false,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 15',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 16',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 17',
    status: 'active'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 23',
    status: 'pending'
  }, {
    step: 5,
    label: 'Under review',
    date: '—',
    status: 'pending'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'Please address the following objection regarding Private Passenger Auto loss trends.',
  objections: [{
    num: 1,
    status: 'No response filed',
    text: 'The selected annual loss cost trend of +5.5% exceeds the historical 3-year average trend of +3.2%. Please provide additional actuarial justification or industry data to support the higher trend selection.',
    response: null
  }]
}, {
  id: 5,
  serff_tracking_id: 'PERR-134912911',
  company_name: 'Preferred Professional Insurance',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'PL01 Professional Liability',
  submitted_date: 'Apr 16, 2026',
  objection_letter_date: 'Apr 18, 2026',
  respond_by_date: 'Apr 25, 2026',
  objection_letter_status: 'PI Requested',
  total_objections: 2,
  responded: 0,
  isResponded: false,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 16',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 17',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 18',
    status: 'active'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 25',
    status: 'pending'
  }, {
    step: 5,
    label: 'Under review',
    date: '—',
    status: 'pending'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'Please address the following objections regarding Professional Liability loss development.',
  objections: [{
    num: 1,
    status: 'No response filed',
    text: 'The loss development triangles submitted are truncated at 84 months. Please provide full 120-month triangles with tail factor derivations for medical malpractice lines.',
    response: null
  }, {
    num: 2,
    status: 'No response filed',
    text: 'Justify the exclusion of the 2023 accident year loss data from your ultimate loss projections.',
    response: null
  }]
}, {
  id: 6,
  serff_tracking_id: 'DELA-134904423',
  company_name: 'Alpha Dental Programs',
  state: 'Texas',
  filing_type: 'Rate/Rule',
  toi: 'DE01 Dental',
  submitted_date: 'Apr 10, 2026',
  objection_letter_date: 'Apr 12, 2026',
  respond_by_date: 'Apr 18, 2026',
  objection_letter_status: 'Responded',
  total_objections: 1,
  responded: 1,
  isResponded: true,
  timeline: [{
    step: 1,
    label: 'Submitted',
    date: 'Apr 10',
    status: 'done'
  }, {
    step: 2,
    label: 'Assigned',
    date: 'Apr 11',
    status: 'done'
  }, {
    step: 3,
    label: 'Objection issued',
    date: 'Apr 12',
    status: 'done'
  }, {
    step: 4,
    label: 'Response due',
    date: 'Apr 18',
    status: 'done'
  }, {
    step: 5,
    label: 'Under review',
    date: 'Apr 19',
    status: 'active'
  }, {
    step: 6,
    label: 'Decision',
    date: '—',
    status: 'pending'
  }],
  intro: 'The Texas Department of Insurance requires clarification regarding dental network access fee structures.',
  objections: [{
    num: 1,
    status: 'Response filed Apr 18',
    text: 'Provide support for the proposed 12% increase in network access fees for individual policyholders.',
    response: 'We have updated Exhibit D with a complete cost exhibit detailing provider contract rate changes in Texas over the last 18 months, justifying the fee adjustment.'
  }]
}];
const Objections = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const formatValue = (val, limit = 15) => {
    if (!val || val === '—') return '—';
    const str = String(val);
    if (str.length <= limit) return str;
    return (
      <span
        onMouseMove={(e) => setMapTooltip({ x: e.clientX, y: e.clientY, content: str })}
        onMouseLeave={() => setMapTooltip(null)}
        style={{ cursor: 'help' }}
      >
        {str.substring(0, limit)}...
      </span>
    );
  };

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiling, setSelectedFiling] = useState(null);
  const lastFilingRef = useRef(null);
  if (selectedFiling) lastFilingRef.current = selectedFiling;
  const activeFiling = selectedFiling || lastFilingRef.current || { objections: [], serff_tracking_id: '', company_name: '', toi: '', filing_type: '', state: '', intro: '', objection_letter_status: '' };
  const [mapTooltip, setMapTooltip] = useState(null);
  const [activePageTab, setActivePageTab] = useState(() => {
    if (typeof window === 'undefined') return 'Insights';
    const p = new URLSearchParams(window.location.search).get('tab');
    return p === 'Table' ? 'Table' : 'Insights';
  });
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState(null);

  useEffect(() => {
    const fetchObjections = async () => {
      try {
        setLoading(true);
        const res = await getObjections();
        if (res?.data?.success && res?.data?.data && res?.data?.data?.length > 0) {
          const normalized = res.data.data.map((item, index) => {
            const timeline = [
              { step: 1, label: 'Submitted', date: item.SerffFilingSnapshot?.submission_date || 'Apr 15', status: 'done' },
              { step: 2, label: 'Assigned', date: 'Apr 16', status: 'done' },
              { step: 3, label: 'Objection issued', date: item.respond_by_date || 'Apr 17', status: 'done' },
              { step: 4, label: 'Response due', date: item.respond_by_date || 'Apr 23', status: 'active' },
              { step: 5, label: 'Under review', date: '—', status: 'pending' },
              { step: 6, label: 'Decision', date: '—', status: 'pending' }
            ];

            const objections = [
              {
                num: 1,
                status: item.response_text ? 'Responded' : 'No response filed',
                text: item.objection_text || 'Actuarial memorandum or justification requested.',
                response: item.response_text || null
              }
            ];

            return {
              id: item.id || index + 1,
              serff_tracking_id: item.SerffFilingSnapshot?.serff_tracking_number || 'AMSI-134913394',
              company_name: item.company_names || 'ASI Lloyds',
              state: item.state || 'Texas',
              filing_type: item.filing_type || 'Rate/Rule',
              toi: item.SerffFilingSnapshot?.toi || item.toi || 'HO03 Homeowners',
              sub_toi: item.SerffFilingSnapshot?.sub_toi || item.sub_toi || '',
              submitted_date: item.SerffFilingSnapshot?.submission_date || 'Apr 15, 2026',
              objection_letter_date: item.respond_by_date || 'Apr 17, 2026',
              respond_by_date: item.respond_by_date || 'Apr 23, 2026',
              objection_letter_status: item.SerffFilingSnapshot?.status || (item.response_text ? 'Responded' : 'PI Requested'),
              total_objections: objections.length,
              responded: item.response_text ? 1 : 0,
              isResponded: !!item.response_text,
              timeline,
              intro: 'The department requires clarification regarding the submitted filing.',
              objections
            };
          });
          setApiData(normalized);
        } else {
          setApiData(OBJECTIONS_DATA);
        }
      } catch (e) {
        console.error('Error fetching objections from API:', e);
        setApiData(OBJECTIONS_DATA);
      } finally {
        setLoading(false);
      }
    };
    fetchObjections();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && (tabParam === 'Insights' || tabParam === 'Table' || tabParam === 'Answers' || tabParam === 'Report')) {
      setActivePageTab(tabParam);
    }
  }, [location.search]);

  // Dynamic filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('All states');
  const [filterStatus, setFilterStatus] = useState('All statuses');
  const [filterType, setFilterType] = useState('All filing types');
  const [filterToi, setFilterToi] = useState('All TOI');
  const [filterCompany, setFilterCompany] = useState('All companies');
  const [filterResponse, setFilterResponse] = useState('All');
  const [openFilter, setOpenFilter] = useState(null);
  const [subStart, setSubStart] = useState(null);
  const [subEnd, setSubEnd] = useState(null);
  const [tempSubRange, setTempSubRange] = useState([null, null]);
  const [showTableFilters, setShowTableFilters] = useState(false);

  const [objTablePage, setObjTablePage] = useState(1);
  const [objRowsPerPage, setObjRowsPerPage] = useState(25);
  const [objPageInput, setObjPageInput] = useState('1');
  const filterClickCount = useRef(0);

  const matchesDrawerValue = (filterVal, defaultVal, rowVal, catalog = null) => {
    if (!filterVal || filterVal === defaultVal) return true;
    const selected = parseMultiSelect(filterVal, defaultVal, catalog);
    if (!selected.length) return true;
    return selected.some((v) => String(rowVal || '').toLowerCase() === v.toLowerCase()
      || String(rowVal || '').toLowerCase().includes(v.toLowerCase()));
  };

  const hasFiltersApplied = filterState !== 'All states' ||
    filterStatus !== 'All statuses' ||
    filterType !== 'All filing types' ||
    filterToi !== 'All TOI' ||
    filterCompany !== 'All companies' ||
    filterResponse !== 'All' ||
    subStart !== null ||
    subEnd !== null;

  const states = useMemo(() => {
    const u = [...new Set(apiData.map((f) => f.state).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All states', ...u];
  }, [apiData]);
  const statuses = useMemo(() => {
    const u = [...new Set(apiData.map((f) => f.objection_letter_status).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All statuses', ...u];
  }, [apiData]);
  const filingTypes = useMemo(() => {
    const u = [...new Set(apiData.map((f) => f.filing_type).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All filing types', ...u];
  }, [apiData]);
  const tois = useMemo(() => {
    const u = [...new Set(apiData.map((f) => f.toi).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All TOI', ...u];
  }, [apiData]);
  const companies = useMemo(() => {
    const u = [...new Set(apiData.map((f) => f.company_name).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All companies', ...u];
  }, [apiData]);
  const drawerOptions = useMemo(() => ({
    states: states.filter(s => s !== 'All states'),
    filingTypes: filingTypes.filter(t => t !== 'All filing types'),
    companies: companies.filter(c => c !== 'All companies')
  }), [states, filingTypes, companies]);
  const drawerStatuses = useMemo(() => statuses.filter(s => s !== 'All statuses'), [statuses]);
  const drawerTois = useMemo(() => tois.filter(t => t !== 'All TOI'), [tois]);

  useEffect(() => {
    if (activePageTab !== 'Insights') return;
    const fmt = (d) => {
      if (!d || !(d instanceof Date)) return undefined;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    let cancelled = false;
    const run = async () => {
      setDashLoading(true);
      setDashError(null);
      try {
        const payload = {};
        const df = fmt(subStart);
        const dt = fmt(subEnd);
        if (df && dt) {
          payload.date_from = df;
          payload.date_to = dt;
        }

        if (filterState && filterState !== 'All states') payload.state = filterState;
        const companyParam = toApiCompanyQueryParam(filterCompany, 'All companies', drawerOptions.companies);
        if (companyParam) payload.company = companyParam;
        const statusParam = toApiScalarFilter(filterStatus, 'All statuses');
        if (statusParam && statusParam !== 'All statuses') payload.status = statusParam;
        const typeParam = toApiScalarFilter(filterType, 'All filing types');
        if (typeParam && typeParam !== 'All filing types') payload.type = typeParam;
        const toiParam = toApiScalarFilter(filterToi, 'All TOI');
        if (toiParam && toiParam !== 'All TOI') payload.toi = toiParam;
        if (filterResponse && filterResponse !== 'All') {
          payload.response_filter = parseMultiSelect(filterResponse, 'All', ['Responded', 'No response filed']).join(',');
        }

        const res = await getObjectionDashboard(payload);
        if (!cancelled) setDashData(res?.data?.data || null);
      } catch (e) {
        if (!cancelled) {
          setDashError(e?.response?.data?.error || e.message || 'Request failed');
          setDashData(null);
        }
      } finally {
        if (!cancelled) setDashLoading(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePageTab, subStart, subEnd, filterState, filterCompany, filterStatus, filterType, filterToi, filterResponse]);

  const filteredFilings = apiData.filter(f => {
    const matchesSearch = f.serff_tracking_id.toLowerCase().includes(searchTerm.toLowerCase()) || f.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesState = matchesDrawerValue(filterState, 'All states', f.state, drawerOptions.states);
    const matchesStatus = matchesDrawerValue(filterStatus, 'All statuses', f.objection_letter_status, drawerStatuses);
    const matchesType = matchesDrawerValue(filterType, 'All filing types', f.filing_type, drawerOptions.filingTypes);
    const matchesToi = matchesDrawerValue(filterToi, 'All TOI', f.toi, drawerTois);
    const matchesCompany = matchesDrawerValue(filterCompany, 'All companies', f.company_name, drawerOptions.companies);
    const responseOptions = filterResponse === 'All' ? [] : parseMultiSelect(filterResponse, 'All', ['Responded', 'No response filed']);
    const matchesResponse = filterResponse === 'All' ||
      (responseOptions.includes('Responded') && f.isResponded) ||
      (responseOptions.includes('No response filed') && !f.isResponded);

    return matchesSearch && matchesState && matchesStatus && matchesType && matchesToi && matchesCompany && matchesResponse;
  });

  const objectionTotalRows = filteredFilings.length;
  const objectionTotalPages = Math.ceil(objectionTotalRows / objRowsPerPage) || 1;
  const objectionPageRows = useMemo(() => {
    const start = (objTablePage - 1) * objRowsPerPage;
    return filteredFilings.slice(start, start + objRowsPerPage);
  }, [filteredFilings, objTablePage, objRowsPerPage]);

  useEffect(() => {
    setObjTablePage(1);
  }, [searchTerm, filterState, filterStatus, filterType, filterToi, filterCompany, filterResponse]);

  useEffect(() => {
    setObjPageInput(objTablePage.toString());
  }, [objTablePage]);

  const handleObjPageInputSubmit = (e) => {
    e.preventDefault();
    const val = parseInt(objPageInput, 10);
    if (!Number.isNaN(val) && val >= 1 && val <= objectionTotalPages) {
      setObjTablePage(val);
    } else {
      setObjPageInput(objTablePage.toString());
    }
  };

  const formatTableDate = (dateStr) => {
    if (!dateStr || dateStr === '—') return '—';
    try {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusStyle = status => {
    if (!status) return { background: 'var(--c-white)', color: 'var(--text2)' };
    const s = String(status).toLowerCase();
    
    // 1. Completed States -> Timeline Emerald
    if (s.includes('approved') || s.includes('accepted') || s.includes('respond') || s.includes('complete')) {
      return { background: 'var(--bg-teal-soft)', color: 'var(--c-teal-active)' };
    }
    // 2. Attention/Negative States -> Timeline Orange
    if (s.includes('objection') || s.includes('info') || s.includes('question') || s.includes('reject') || s.includes('disapproved') || s.includes('withdrawn') || s.includes('closed') || s.includes('pi') || s.includes('open') || s.includes('request')) {
      return { background: 'var(--bg-orange-soft)', color: 'var(--c-orange)' };
    }
    // 3. Active/In-Progress States -> Timeline Purple
    if (s.includes('review') || s.includes('pending')) {
      return { background: 'var(--bg-purple-soft)', color: 'var(--c-purple-1)' };
    }
    
    return { background: 'var(--bg-blue-soft)', color: 'var(--c-blue-1)' };
  };
  const handleSelectFiling = async (filing) => {
    try {
      const res = await getObjections(filing.id);
      if (res?.data?.success && res?.data?.data) {
        const { summary, details } = res.data.data;
        const timeline = [
          { step: 1, label: 'Submitted', date: summary.SerffFilingSnapshot?.submission_date || 'Apr 15', status: 'done' },
          { step: 2, label: 'Assigned', date: 'Apr 16', status: 'done' },
          { step: 3, label: 'Objection issued', date: summary.respond_by_date || 'Apr 17', status: 'done' },
          { step: 4, label: 'Response due', date: summary.respond_by_date || 'Apr 23', status: summary.response_text ? 'done' : 'active' },
          { step: 5, label: 'Under review', date: '—', status: 'pending' },
          { step: 6, label: 'Decision', date: '—', status: 'pending' }
        ];
        const objections = details.map((obj, i) => ({
          num: obj.objection_number || i + 1,
          status: obj.response_text ? 'Responded' : 'No response filed',
          text: obj.objection_text || 'Actuarial memorandum or justification requested.',
          response: obj.response_text || null
        }));
        const snap = summary.SerffFilingSnapshot || {};
        setSelectedFiling({
          id: summary.id,
          serff_tracking_id: snap.serff_tracking_number || summary.serff_tracking_id || 'AMSI-134913394',
          company_name: summary.company_names || summary.company_name || 'ASI Lloyds',
          state: summary.state || summary.jurisdiction || 'Texas',
          filing_type: summary.filing_type || 'Rate/Rule',
          toi: summary.toi || 'HO03 Homeowners',
          sub_toi: snap.sub_toi || '—',
          naic_code: snap.naic_code || '—',
          submitted_date: snap.submission_date || 'Apr 15, 2026',
          objection_letter_date: summary.respond_by_date || 'Apr 17, 2026',
          respond_by_date: summary.respond_by_date || 'Apr 23, 2026',
          disposition_date: snap.disposition_date || null,
          fetched_at: snap.fetched_at || null,
          state_status_last_changed: snap.state_status_last_changed || null,
          total_docs: snap.total_docs || 0,
          form_docs: snap.form_docs || 0,
          rate_rule_docs: snap.rate_rule_docs || 0,
          supporting_docs: snap.supporting_docs || 0,
          correspondence_count: snap.correspondence_count || 0,
          objection_letter_status: snap.status || (summary.response_text ? 'Responded' : 'PI Requested'),
          total_objections: objections.length,
          responded: objections.filter(o => o.response).length,
          isResponded: objections.every(o => o.response),
          timeline,
          intro: summary.objection_text ? 'The department requires clarification regarding the submitted filing.' : 'No introduction present.',
          objections
        });
      } else {
        setSelectedFiling(filing);
      }
    } catch (e) {
      console.error('Error fetching specific objection details:', e);
      setSelectedFiling(filing);
    }
  };
  return <>
    <PageHeader
      title="Objections"
      activePageTab={activePageTab}
      searchTerm={searchTerm}
      onSearchChange={e => {
        setSearchTerm(e.target.value);
        setObjTablePage(1);
      }}
      hasFiltersApplied={hasFiltersApplied}
      onClearFilters={() => {
        setFilterState('All states');
        setFilterStatus('All statuses');
        setFilterType('All filing types');
        setFilterToi('All TOI');
        setFilterCompany('All companies');
        setFilterResponse('All');
        setSubStart(null);
        setSubEnd(null);
        setTempSubRange([null, null]);
        setObjTablePage(1);
      }}
      showTableFilters={showTableFilters}
      onToggleFilters={() => setShowTableFilters(!showTableFilters)}
    />

    {/* <div className="page-tabs-container">
      <div className="page-tabs">
        <div className={`page-tab ${activePageTab === 'Insights' ? 'active' : ''}`} onClick={() => handleTabClick('Insights')} role="presentation">
          Insights
        </div>
        <div className={`page-tab ${activePageTab === 'Table' ? 'active' : ''}`} onClick={() => handleTabClick('Table')} role="presentation">
          Table
        </div>
      </div>
    </div> */}

    {activePageTab === 'Table' ? <>
      <div className="body">
        <div className="table-area">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="col-tn"><div className="th-sort">Tracking #</div></th>
                  <th className="col-company"><div className="th-sort">Company</div></th>
                  <th className="col-state">State</th>
                  <th className="col-type">Filing type</th>
                  <th className="col-toi">TOI</th>
                  <th className="col-status"><div className="th-sort">SERFF status</div></th>
                  <th className="col-status">Response</th>
                  <th className="col-date"><div className="th-sort">Submitted</div></th>
                  <th className="col-date">Respond by</th>
                  <th className="col-date">Letter date</th>
                </tr>
              </thead>
              <tbody>
                {loading && apiData.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)' }}>
                      Loading objectionsâ€¦
                    </td>
                  </tr>
                ) : objectionPageRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)' }}>
                      No filings with objections match filters.
                    </td>
                  </tr>
                ) : (
                  objectionPageRows.map((s) => (
                    <tr
                      key={s.id}
                      className={selectedFiling && selectedFiling.id === s.id ? 'selected' : ''}
                      onClick={() => handleSelectFiling(s)}
                    >
                      <td><span className="tn">{formatValue(s.serff_tracking_id)}</span></td>
                      <td className="company-cell">
                        <div className="company-name">{formatValue(s.company_name)}</div>
                      </td>
                      <td>{s.state}</td>
                      <td>{formatValue(s.filing_type)}</td>
                      <td>{formatValue(s.toi)}</td>
                      <td><span className="pill" style={getStatusStyle(s.objection_letter_status)}>{s.objection_letter_status}</span></td>
                      <td><span className="pill" style={getStatusStyle(s.isResponded ? 'Responded' : 'Open')}>{s.isResponded ? 'Responded' : 'Open'}</span></td>
                      <td className="date-cell">{formatTableDate(s.submitted_date)}</td>
                      <td className="date-cell">{formatTableDate(s.respond_by_date)}</td>
                      <td className="date-cell">{formatTableDate(s.objection_letter_date)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination pagination-sticky">
            <div className="pg-info">
              Showing {objectionTotalRows === 0 ? 0 : (objTablePage - 1) * objRowsPerPage + 1}–{Math.min(objTablePage * objRowsPerPage, objectionTotalRows)} of {objectionTotalRows} filings with objections
            </div>
            <div className="table-pagination">
              <div className="table-pagination__control">
                <span className="table-pagination__label">Rows per page</span>
                <div className={`select-wrap ${openFilter === 'obj-rows' ? 'is-open' : ''}`}>
                  <span className="select-measure table-pagination__measure">{objRowsPerPage}</span>
                  <select
                    className="filter-select table-pagination__select"
                    value={objRowsPerPage}
                    onFocus={() => {
                      filterClickCount.current = 0;
                      setOpenFilter('obj-rows');
                    }}
                    onBlur={() => {
                      filterClickCount.current = 0;
                      setOpenFilter(null);
                    }}
                    onClick={(e) => {
                      filterClickCount.current += 1;
                      if (filterClickCount.current > 1) {
                        e.target.blur();
                      }
                    }}
                    onChange={(e) => {
                      setObjRowsPerPage(parseInt(e.target.value, 10));
                      setObjTablePage(1);
                      e.target.blur();
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={35}>35</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="pg-controls">
                <button type="button" className="pg-btn" disabled={objTablePage === 1} onClick={() => setObjTablePage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft size={14} />
                </button>
                <form onSubmit={handleObjPageInputSubmit} className="companydetails-style-33">
                  <input
                    type="text"
                    className="pg-input"
                    value={objPageInput}
                    onChange={(e) => setObjPageInput(e.target.value)}
                    onBlur={() => setObjPageInput(objTablePage.toString())}
                  />
                  <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
                </form>
                <button
                  type="button"
                  className="pg-btn"
                  disabled={objTablePage === objectionTotalPages || objectionTotalPages === 0}
                  onClick={() => setObjTablePage((p) => Math.min(objectionTotalPages, p + 1))}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={`drawer ${selectedFiling ? 'drawer-open' : ''}`}>
          {(() => {
            const s = activeFiling;
            const docTotal = (s.form_docs || 0) + (s.rate_rule_docs || 0) + (s.supporting_docs || 0) + (s.correspondence_count || 0);
            return (
              <>
                <div className="drawer-header">
                  <div>
                    <div className="drawer-tn" style={{ fontSize: '13px' }}>{s.serff_tracking_id}</div>
                    <div className="drawer-company" style={{ fontSize: '18px', marginTop: '4px' }}>{s.company_name}</div>
                    <div className="drawer-toi" style={{ fontSize: '13px', marginTop: '4px' }}>{s.toi || '—'} — {s.filing_type} · {s.state}</div>
                  </div>
                  <button className="drawer-close filters-drawer-close" onClick={() => setSelectedFiling(null)}>×</button>
                </div>
                <div className="drawer-tabs">
                  <div className="dt active">Details</div>
                </div>
                <div className="drawer-body">
                  <div className="detail-section">
                    <div className="ds-title" style={{ fontSize: '12px', marginBottom: '6px' }}>Summary</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className="pill" style={{ ...getStatusStyle(s.objection_letter_status), fontSize: '12px', padding: '4px 10px' }}>{s.objection_letter_status}</span>
                      <span className="pill" style={{ background: 'var(--c-white)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: '12px', padding: '4px 10px' }}>{s.total_objections} objection{s.total_objections !== 1 ? 's' : ''}</span>
                      <span style={{ fontSize: '12px', color: 'var(--text3)' }}>Respond by {formatTableDate(s.respond_by_date)}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title" style={{ fontSize: '12px', marginBottom: '6px' }}>Introduction</div>
                    <div style={{ fontSize: '14px', color: 'var(--text2)', lineHeight: 1.5 }}>{s.intro}</div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title" style={{ fontSize: '12px', marginBottom: '6px' }}>Timeline</div>
                    <div className="field-grid">
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Submission date</div>
                        <div className="field-val">{formatTableDate(s.submitted_date)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Respond by</div>
                        <div className="field-val">{formatTableDate(s.respond_by_date)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Objection issued</div>
                        <div className="field-val">{formatTableDate(s.objection_letter_date)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Disposition date</div>
                        <div className="field-val" style={{ color: s.disposition_date ? 'var(--c-black)' : 'var(--text3)' }}>
                          {formatTableDate(s.disposition_date) || 'Not set'}
                        </div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Last updated</div>
                        <div className="field-val">{formatTableDate(s.fetched_at) || '—'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title" style={{ fontSize: '12px', marginBottom: '6px' }}>Filing info</div>
                    <div className="field-grid">
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>State</div>
                        <div className="field-val">{s.state}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>NAIC code</div>
                        <div className="field-val">{s.naic_code || '—'}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Filing type</div>
                        <div className="field-val">{s.filing_type}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>TOI</div>
                        <div className="field-val">{s.toi || '—'}</div>
                      </div>
                      <div className="field">
                        <div className="field-label" style={{ fontSize: '12px' }}>Sub TOI</div>
                        <div className="field-val">{s.sub_toi || '—'}</div>
                      </div>
                    </div>
                  </div>

                  {docTotal > 0 && (
                    <div className="detail-section">
                      <div className="ds-title" style={{ fontSize: '12px', marginBottom: '6px' }}>Documents — {docTotal} total</div>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', paddingTop: '8px' }}>
                        <div className="pb-col">
                          <div className="pb-bar" style={{ height: `${Math.min(40, (s.form_docs || 0) * 3 + 4)}px`, background: 'var(--bg-blue-soft)', border: '1px solid var(--c-blue-1)' }}></div>
                          <div className="pb-label" style={{ color: 'var(--c-blue-1)' }}>Form</div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.form_docs || 0}</div>
                        </div>
                        <div className="pb-col">
                          <div className="pb-bar" style={{ height: `${Math.min(40, (s.rate_rule_docs || 0) * 3 + 4)}px`, background: 'var(--bg-purple-soft)', border: '1px solid var(--c-purple-1)' }}></div>
                          <div className="pb-label" style={{ color: 'var(--c-purple-1)' }}>Rate</div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.rate_rule_docs || 0}</div>
                        </div>
                        <div className="pb-col">
                          <div className="pb-bar" style={{ height: `${Math.min(40, (s.supporting_docs || 0) * 3 + 4)}px`, background: 'var(--bg-teal-soft)', border: '1px solid var(--c-teal-active)' }}></div>
                          <div className="pb-label" style={{ color: 'var(--c-teal-active)' }}>Supp</div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.supporting_docs || 0}</div>
                        </div>
                        <div className="pb-col">
                          <div className="pb-bar" style={{ height: `${Math.min(40, (s.correspondence_count || 0) * 3 + 4)}px`, background: 'var(--bg-orange-soft)', border: '1px solid var(--c-orange)' }}></div>
                          <div className="pb-label" style={{ color: 'var(--c-orange)' }}>Corr</div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.correspondence_count || 0}</div>
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                <div className="drawer-actions">
                  {/* <button
                    className="btn btn-sm"
                    onClick={() => navigate(`/filings/${encodeURIComponent(s.serff_tracking_id)}`)}
                  >
                    Open Filing Details
                  </button> */}
                  <button
                    className="btn btn-sm btn-filters"
                    onClick={() => {
                      sessionStorage.setItem('comply_last_objections_path', location.pathname);
                      sessionStorage.setItem('comply_active_objections_tab', activePageTab);
                      navigate(`/objections/${encodeURIComponent(s.id)}`);
                    }}
                  >
                    More Info
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </> : activePageTab === 'Insights' ? <div className="body insights-panel">
      <div className="content" style={{ maxWidth: '100%', paddingTop: '22px' }}>
        <ObjectionDashboardPanel data={dashData} loading={dashLoading} error={dashError} />
      </div>
    </div> : <div className="body page-empty">
      <div className="page-empty__inner">
        <h2 className="page-empty__title">{activePageTab}</h2>
        <p className="page-empty__text">Coming Soon...</p>
      </div>
    </div>}
    <FiltersDrawer
      isOpen={showTableFilters}
      onClose={() => setShowTableFilters(false)}
      filterState={filterState}
      setFilterState={setFilterState}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      filterType={filterType}
      setFilterType={setFilterType}
      filterToi={filterToi}
      setFilterToi={setFilterToi}
      filterCompany={filterCompany}
      setFilterCompany={setFilterCompany}
      filterResponse={filterResponse}
      setFilterResponse={setFilterResponse}
      options={drawerOptions}
      statuses={drawerStatuses}
      tois={drawerTois}
      setCurrentPage={setObjTablePage}
      isInsights={activePageTab === 'Insights'}
      subStart={subStart}
      setSubStart={setSubStart}
      subEnd={subEnd}
      setSubEnd={setSubEnd}
      tempSubRange={tempSubRange}
      setTempSubRange={setTempSubRange}
      submissionDateTitle="Date range"
      showSubmissionDates
    />
    {mapTooltip && (
      <div
        className="map-hover-tooltip"
        style={{
          top: mapTooltip.y + 15,
          left: mapTooltip.x + 15
        }}
      >
        {mapTooltip.content}
      </div>
    )}
  </>;

};
export default Objections;

